import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'

// Initialize Sentry for error tracking
Sentry.init({
  // Use DSN from environment variable
  dsn: import.meta.env.VITE_SENTRY_DSN_DEV,
  
  // Only enable if DSN is provided
  enabled: !!import.meta.env.VITE_SENTRY_DSN_DEV,
  
  // Set environment from environment variable
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
  
  // Release version for tracking deployments
  release: `cwy-booking-frontend@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,
  
  // Setting this option to true will send default PII data to Sentry
  sendDefaultPii: true,
  
  // Sample rate for performance monitoring (1.0 = 100%, 0.1 = 10%)
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  
  // Sample rate for session replay (1.0 = 100%, 0.1 = 10%)
  replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  
  // Sample rate for error sessions (always capture errors)
  replaysOnErrorSampleRate: 1.0,
  
  // Enable debug mode only in development
  debug: import.meta.env.DEV,
  
  // Filter out localhost errors in production (optional)
  beforeSend(event) {
    // In production, you might want to filter certain errors
    if (import.meta.env.PROD) {
      // Don't send errors from localhost in production
      if (event.request?.url?.includes('localhost')) {
        return null;
      }
    }
    return event;
  },
  
  // Integrations
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Mask all text content for privacy
      maskAllText: true,
      // Block all media for privacy
      blockAllMedia: true,
    }),
  ],
  
  // Performance monitoring - trace propagation
  // IMPORTANT: Only propagate traces to frontend domains, NOT API domains
  // This prevents CORS errors from sentry-trace headers on API requests
  // Only include frontend domain, explicitly exclude api.canadawaleyar.com
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/appointment\.canadawaleyar\.com$/,
  ],
})

// Capture unhandled promise rejections (network errors, CORS errors, etc.)
window.addEventListener('unhandledrejection', (event) => {
  // Check if it's a network/CORS error
  const error = event.reason;
  
  if (error instanceof TypeError && error.message.includes('fetch')) {
    Sentry.captureException(error, {
      tags: {
        errorType: 'network_error',
      },
      extra: {
        message: error.message,
        stack: error.stack,
      },
    });
  } else if (error && typeof error === 'object' && 'message' in error) {
    // Capture other unhandled promise rejections
    Sentry.captureException(error);
  }
});

// Capture global errors (including CORS errors that bubble up)
window.addEventListener('error', (event) => {
  // Check if it's a CORS or network error
  if (
    event.message?.includes('CORS') ||
    event.message?.includes('fetch') ||
    event.message?.includes('Failed to fetch') ||
    event.message?.includes('NetworkError')
  ) {
    Sentry.captureException(event.error || new Error(event.message), {
      tags: {
        errorType: 'cors_or_network_error',
      },
      extra: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  }
});

// Intercept fetch errors to capture network/CORS errors
const originalFetch = window.fetch;
window.fetch = async (...args: Parameters<typeof fetch>) => {
  try {
    const response = await originalFetch(...args);
    
    // If response is not ok, capture it
    if (!response.ok) {
      const url = typeof args[0] === 'string' 
        ? args[0] 
        : args[0] instanceof URL 
          ? args[0].toString()
          : args[0].url;
      Sentry.captureMessage(`API request failed: ${response.status} ${response.statusText}`, {
        level: 'warning',
        tags: {
          errorType: 'api_error',
          statusCode: response.status,
        },
        extra: {
          url,
          status: response.status,
          statusText: response.statusText,
        },
      });
    }
    
    return response;
  } catch (error: unknown) {
    // Capture network errors (CORS, network failures, etc.)
    const url = typeof args[0] === 'string' 
      ? args[0] 
      : args[0] instanceof URL 
        ? args[0].toString()
        : args[0]?.url || 'unknown';
    
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isCorsError = errorMessage.includes('CORS') || errorMessage.includes('Failed to fetch');
    
    Sentry.captureException(error instanceof Error ? error : new Error(errorMessage), {
      tags: {
        errorType: 'fetch_error',
        isCorsError: isCorsError.toString(),
      },
      extra: {
        url,
        method: args[1]?.method || 'GET',
        errorMessage,
      },
    });
    
    // Re-throw the error so the calling code can handle it
    throw error;
  }
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
